import { QuantityResult } from '../types';

export class CountUnitConverter {
  // 싱글턴 인스턴스
  private static instance: CountUnitConverter;

  private constructor() {}

  static getInstance(): CountUnitConverter {
    if (!CountUnitConverter.instance) {
      CountUnitConverter.instance = new CountUnitConverter();
    }
    return CountUnitConverter.instance;
  }

  protected units = ['', 'K', 'M', 'G', 'T', 'P', 'E'] as const;

  protected unitToBase: Record<(typeof this.units)[number], number> = {
    '': 1,
    K: 1_000,
    M: 1_000_000,
    G: 1_000_000_000,
    T: 1_000_000_000_000,
    P: 1_000_000_000_000_000,
    E: 1_000_000_000_000_000_000,
  };

  public isValidUnit(unit: string): unit is (typeof this.units)[number] {
    return this.units.includes(unit as (typeof this.units)[number]);
  }

  // big.js 콜드경로(드문 입력 전용 경로)에서 카운트 단위(K/M/G/…)의 스케일 배수를 얻기 위해 노출한다.
  public getUnitRatio(unit: string): number | undefined {
    return this.isValidUnit(unit) ? this.unitToBase[unit] : undefined;
  }

  public convertToUnit(value: number, fromUnit: string, toUnit: string): QuantityResult {
    const validFromUnit = this.isValidUnit(fromUnit) ? fromUnit : '';
    const validToUnit = this.isValidUnit(toUnit) ? toUnit : validFromUnit;

    // 동일 단위는 FP 곱·나누기 없이 그대로 패스
    if (validFromUnit === validToUnit) {
      return {
        quantity: value,
        countUnit: validToUnit,
        unit: this.isValidUnit(toUnit) ? '' : toUnit,
      };
    }

    const baseValue = value * this.unitToBase[validFromUnit];

    return {
      quantity: baseValue / this.unitToBase[validToUnit],
      countUnit: validToUnit,
      unit: this.isValidUnit(toUnit) ? '' : toUnit,
    };
  }

  public convertToBestUnit(value: number, fromUnit: string): QuantityResult {
    const validFromUnit = this.isValidUnit(fromUnit) ? fromUnit : '';

    // value가 0이면 fromUnit 그대로 반환
    if (value === 0) return { quantity: value, countUnit: validFromUnit, unit: '' };

    const fromRatio = this.unitToBase[validFromUnit];
    const baseValue = value * fromRatio;
    const absBase = Math.abs(baseValue);

    for (let i = this.units.length - 1; i >= 0; i--) {
      const unit = this.units[i];
      const ratio = this.unitToBase[unit];
      if (absBase >= ratio) {
        return {
          quantity: unit === validFromUnit ? value : baseValue / ratio,
          countUnit: unit,
          unit: this.isValidUnit(fromUnit) ? '' : fromUnit,
        };
      }
    }

    const smallest = this.units[0];
    return {
      quantity: smallest === validFromUnit ? value : baseValue / this.unitToBase[smallest],
      countUnit: smallest,
      unit: this.isValidUnit(fromUnit) ? '' : fromUnit,
    };
  }

  /**
   * 기존 convertValueWithUnitList와 동일한 로직으로 큰 단위로만 변환
   * 작은 단위로는 변환하지 않음
   */
  public convertToLargerBestUnit(value: number, fromUnit: string): QuantityResult {
    const validFromUnit = this.isValidUnit(fromUnit) ? fromUnit : '';

    // value가 0이면 fromUnit 그대로 반환
    if (value === 0) return { quantity: value, countUnit: validFromUnit, unit: '' };

    const fromRatio = this.unitToBase[validFromUnit];
    const baseValue = value * fromRatio;
    const absBase = Math.abs(baseValue);

    // 현재 단위의 인덱스 찾기
    let unitIndex = this.units.findIndex((unit) => unit === validFromUnit);
    const startIndex = unitIndex;

    // 큰 단위로만 변환 (작은 단위로는 변환하지 않음)
    while (unitIndex < this.units.length - 1) {
      const nextUnit = this.units[unitIndex + 1];
      const nextUnitBase = this.unitToBase[nextUnit];

      if (absBase >= nextUnitBase) {
        unitIndex++;
      } else {
        break;
      }
    }

    const targetUnit = this.units[unitIndex];

    return {
      quantity: unitIndex === startIndex ? value : baseValue / this.unitToBase[targetUnit],
      countUnit: targetUnit,
      unit: this.isValidUnit(fromUnit) ? '' : fromUnit,
    };
  }
}
